import { Component, type ErrorInfo, type ReactNode } from "react";
import { Phone, RefreshCw } from "lucide-react";
import logo from "@/assets/arcondes-logo.png";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

export default class RouteErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Page load failed", error, info.componentStack);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <main className="min-h-screen bg-secondary flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-xl bg-background border border-border rounded-sm p-8 sm:p-10 text-center shadow-xl">
          <img src={logo} alt="ARCONDES" className="h-12 w-auto mx-auto mb-8" />
          <div className="h-1 w-12 bg-brand-yellow mx-auto mb-5" />
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">This page did not load correctly.</h1>
          <p className="text-muted-foreground leading-relaxed mb-7">
            Refresh the page to load the latest version. If you need immediate help, call ARCONDES directly.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-flex items-center justify-center gap-2 bg-brand-yellow text-brand-yellow-foreground px-6 py-3 rounded-sm text-sm font-semibold uppercase tracking-wider hover:bg-brand-yellow/90 transition-colors"
            >
              <RefreshCw className="h-4 w-4" /> Refresh page
            </button>
            <a
              href="tel:+13476131717"
              className="inline-flex items-center justify-center gap-2 border border-border bg-background text-foreground px-6 py-3 rounded-sm text-sm font-semibold uppercase tracking-wider hover:border-brand-yellow transition-colors"
            >
              <Phone className="h-4 w-4" /> 347-613-1717
            </a>
          </div>
        </div>
      </main>
    );
  }
}
